import { useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Icon,
  Loading,
  Pill,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import { money, orderStatusLabel, orderStatusTone } from '../lib/format';
import type { CatalogItem, Merchant, Order, Paged } from '../lib/types';
import { Stepper } from './Equipment';

interface LowPaper {
  id: string;
  dba: string;
  mid: string;
  daysLeft: number;
}

export function Supplies() {
  const { user } = useAuth();
  const catalog = useApi<CatalogItem[]>('/catalog?kind=supply');
  const merchants = useApi<Paged<Merchant>>('/merchants?limit=100');
  const lowPaper = useApi<LowPaper[]>('/merchants/low-on-paper?days=10');
  const inTransit = useApi<Paged<Order>>('/orders?kind=supplies&limit=5');

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [merchantId, setMerchantId] = useState('');
  const [rush, setRush] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = catalog.data ?? [];
  const lines = items
    .map((item) => ({ item, quantity: quantities[item.id] ?? 0 }))
    .filter((l) => l.quantity > 0);

  const unitCount = lines.reduce((s, l) => s + l.quantity, 0);
  const subtotal = lines.reduce((s, l) => s + l.item.agentCostCents * l.quantity, 0);
  const shipping = rush ? 3500 : subtotal > 0 ? 1200 : 0;

  async function placeOrder() {
    setPlacing(true);
    setError(null);
    try {
      const merchant = merchants.data?.items.find((m) => m.id === merchantId);
      const order = await api.post<Order>('/orders', {
        kind: 'supplies',
        lines: lines.map((l) => ({ catalogId: l.item.id, quantity: l.quantity })),
        shipTo: merchant?.address ?? {
          street: '[STREET]',
          city: '[CITY]',
          state: 'NY',
          zip: '[ZIP]',
        },
        billedTo: 'node',
        merchantId: merchant?.id,
        merchantName: merchant?.dba,
        rush,
      });
      setPlaced(order);
      setQuantities({});
      inTransit.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That order did not go through.');
    } finally {
      setPlacing(false);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={['Ordering', 'Paper & supplies']}
        title="Paper & supplies"
        actions={<Button>Reorder last shipment</Button>}
      />

      <ScopeBar>
        Agents below you order paper for their own merchants without approval. Anything
        over 20 cases comes to <strong>{user?.nodeName}</strong> first.
      </ScopeBar>

      <PageBody>
        {lowPaper.data && lowPaper.data.length > 0 && (
          <Card className="lowpaper">
            <div className="row">
              <span className="check__icon check__icon--warn">{Icon.alert({ size: 11 })}</span>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <div className="feed__title">
                  {lowPaper.data.length} merchant
                  {lowPaper.data.length === 1 ? '' : 's'} run out of receipt paper within
                  10 days
                </div>
                <div className="feed__sub">
                  Projected from transaction counts since the last shipment ·{' '}
                  {lowPaper.data.map((m) => m.dba).join(', ')}
                </div>
              </div>
              <Button variant="primary">Ship to all {lowPaper.data.length}</Button>
            </div>
          </Card>
        )}

        <div className="split-2">
          <Card>
            <CardHeader title="Catalog" />

            {catalog.loading && <Loading />}
            {catalog.error && <ErrorState error={catalog.error} onRetry={catalog.reload} />}

            {items.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Fits</th>
                      <th>Pack</th>
                      <th className="num">Cost</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="cell-primary">{item.name}</div>
                          <div className="check__detail">{item.description}</div>
                        </td>
                        <td>
                          {item.compatibleWith.length > 0
                            ? item.compatibleWith.join(', ')
                            : '—'}
                        </td>
                        <td>{item.packSize ?? 'Each'}</td>
                        <td className="num">
                          {item.agentCostCents === 0 ? (
                            <Pill tone="ok">No charge</Pill>
                          ) : (
                            money(item.agentCostCents)
                          )}
                        </td>
                        <td>
                          <Stepper
                            label={item.name}
                            value={quantities[item.id] ?? 0}
                            onChange={(q) =>
                              setQuantities((s) => ({ ...s, [item.id]: q }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="stack">
            <Card>
              <CardHeader title="Ship to" />
              <div className="stack" style={{ gap: 12 }}>
                <div className="field">
                  <label className="field__label">Merchant</label>
                  <select
                    className="select"
                    value={merchantId}
                    onChange={(e) => setMerchantId(e.target.value)}
                  >
                    <option value="">My office — stock for the team</option>
                    {(merchants.data?.items ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.dba}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="checkrow">
                  <input
                    type="checkbox"
                    checked={rush}
                    onChange={(e) => setRush(e.target.checked)}
                  />
                  Rush — next-day air
                </label>
              </div>

              <div className="totals">
                <div className="totals__row">
                  <span>{unitCount} item{unitCount === 1 ? '' : 's'}</span>
                  <span>{money(subtotal)}</span>
                </div>
                <div className="totals__row">
                  <span>{rush ? 'Next-day air' : 'Ground shipping'}</span>
                  <span>{money(shipping)}</span>
                </div>
                <div className="totals__row totals__row--grand">
                  <span>Total</span>
                  <span>{money(subtotal + shipping)}</span>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  block
                  disabled={placing || unitCount === 0}
                  onClick={() => void placeOrder()}
                >
                  {placing ? 'Placing…' : 'Place order'}
                </Button>
              </div>

              {error && (
                <div className="notice notice--danger" style={{ marginTop: 12 }}>
                  {Icon.alert({ size: 15 })}
                  <span>{error}</span>
                </div>
              )}
              {placed && (
                <div className="notice notice--ok" style={{ marginTop: 12 }} role="status">
                  {Icon.check({ size: 15 })}
                  <span>
                    Order <strong className="mono">{placed.reference}</strong> placed.
                  </span>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title="In transit" />
              {inTransit.loading && <Loading />}
              {inTransit.data && inTransit.data.items.length === 0 && (
                <Empty title="Nothing in transit" />
              )}
              {inTransit.data && inTransit.data.items.length > 0 && (
                <ul className="feed">
                  {inTransit.data.items.map((o) => (
                    <li className="feed__item feed__item--split" key={o.id}>
                      <div>
                        <div className="feed__title">
                          {o.lines.map((l) => `${l.quantity} × ${l.name}`).join(', ')}
                        </div>
                        <div className="feed__sub">
                          {o.merchantName ?? 'My office'} ·{' '}
                          <span className="mono">{o.reference}</span>
                        </div>
                      </div>
                      <Pill tone={orderStatusTone(o.status)}>
                        {orderStatusLabel(o.status)}
                      </Pill>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}
